import React, { Fragment, useCallback, useEffect, useRef, useState, CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { ChevronLeft, ChevronRight, MoveLeft, MoveRight, PhoneOff, Play } from 'lucide-react';

import { useGenerateAudioResponse, useSynthesizeAudio } from '../../../hooks/mbts-ux';

import { EChatSource } from '../../../types/entities.enum';
import { IChatMessage } from '../../../types/misc.interface';

import { useMBTSConversation } from './ConversationWrapper';

type TAudioSessionStatus = 'idle' | 'listening' | 'processing' | 'preparing' | 'speaking';

interface ISpeechRecognitionAlternative {
    transcript: string;
};

interface ISpeechRecognitionResult {
    isFinal: boolean;
    length: number;
    item: (index: number) => ISpeechRecognitionAlternative;
    [index: number]: ISpeechRecognitionAlternative;
};

interface ISpeechRecognitionResultList {
    length: number;
    item: (index: number) => ISpeechRecognitionResult;
    [index: number]: ISpeechRecognitionResult;
};

interface ISpeechRecognitionEvent extends Event {
    resultIndex: number;
    results: ISpeechRecognitionResultList;
};

interface ISpeechRecognitionErrorEvent extends Event {
    error: string;
};

interface IBrowserSpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    maxAlternatives: number;
    onresult: ((event: ISpeechRecognitionEvent) => void) | null;
    onerror: ((event: ISpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
    onstart: (() => void) | null;
    onspeechstart: (() => void) | null;
    onspeechend: (() => void) | null;
    start: () => void;
    stop: () => void;
    abort: () => void;
};

type TSpeechRecognitionConstructor = new () => IBrowserSpeechRecognition;

const SILENCE_MS = 2200;
const RESTART_MS = 100;
const START_WATCHDOG_MS = 1500;
const DEAD_ENGINE_MS = 10000;
const FLUSH_TIMEOUT_MS = 900;

// Chrome often re-emits overlapping phrases across recognition restarts.
const mergeSpokenText = (prior: string, incoming: string): string => {
    const previous = prior.trim();
    const next = incoming.trim();
    if (!next) return previous;
    if (!previous) return next;
    if (previous === next) return previous;
    if (previous.endsWith(next)) return previous;
    if (next.startsWith(previous) || next.includes(previous)) return next;
    if (previous.includes(next)) return previous;

    const previousWords = previous.split(/\s+/);
    const nextWords = next.split(/\s+/);
    const maxOverlap = Math.min(previousWords.length, nextWords.length);
    for (let size = maxOverlap; size > 0; size -= 1) {
        if (previousWords.slice(-size).join(' ').toLowerCase() === nextWords.slice(0, size).join(' ').toLowerCase()) {
            return [...previousWords, ...nextWords.slice(size)].join(' ');
        };
    };

    return `${previous} ${next}`;
};

type TAudioConvoBgMedia = 'image' | 'video';

const getSpeechRecognitionConstructor = (): TSpeechRecognitionConstructor | null => {
    const speechWindow = window as Window & {
        SpeechRecognition?: TSpeechRecognitionConstructor;
        webkitSpeechRecognition?: TSpeechRecognitionConstructor;
    };

    return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
};

export const MBTSAudioConvoView = () => {
    const {
        scenarioId,
        conversationId,
        personaDetails,
        chats,
        setChats,
        isCompletionWindowVisible,
        setIsCompletionWindowVisible,
        messageThreshold,
        continueMode,
        isProcessingOverview,
        nextOperation,
        nextPersonaDetails,
        evaluateMessageThreshold,
        isHandleNextDisabled,
        handleNext: completeOrPromptNext,
    } = useMBTSConversation();

    const lastMessageRef = useRef<HTMLDivElement>(null);
    const transcriptBodyRef = useRef<HTMLDivElement>(null);
    const recognitionRef = useRef<IBrowserSpeechRecognition | null>(null);
    const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingFinalRef = useRef('');
    const interimTranscriptRef = useRef('');
    const transcriptsRef = useRef<IChatMessage[]>(chats);
    const sessionActiveRef = useRef(false);
    const isMicMutedRef = useRef(false);
    const sessionStatusRef = useRef<TAudioSessionStatus>('idle');
    const isProcessingTurnRef = useRef(false);
    const shouldRestartRecognitionRef = useRef(false);
    const handleUserUtteranceEndRef = useRef<() => Promise<void>>(async () => undefined);
    const startListeningRef = useRef<(options?: { force?: boolean }) => void>(() => undefined);
    const restartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const startWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const deadEngineWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isFlushingRef = useRef(false);
    const isSpeechActiveRef = useRef(false);
    const recognitionStartedRef = useRef(false);
    const lastResultAtRef = useRef(0);
    const messageThresholdRef = useRef(messageThreshold);
    const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
    const typingCleanupRef = useRef<(() => void) | null>(null);

    const [sessionStatus, setSessionStatus] = useState<TAudioSessionStatus>('idle');
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [isMicMuted, setIsMicMuted] = useState(false);
    const [isSpeechSupported, setIsSpeechSupported] = useState(true);
    const [interimTranscript, setInterimTranscript] = useState('');
    const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
    const [personaTypedText, setPersonaTypedText] = useState('');
    // TEMP: preview toggle for env testing — remove after manager review
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [bgMedia, setBgMedia] = useState<TAudioConvoBgMedia>('image');

    const { mutateAsync: generateAudioResponse } = useGenerateAudioResponse();
    const { mutateAsync: synthesizeAudio } = useSynthesizeAudio();

    useEffect(() => {
        messageThresholdRef.current = messageThreshold;
    }, [messageThreshold]);

    useEffect(() => {
        transcriptsRef.current = chats;
    }, [chats]);

    const updateSessionStatus = useCallback((status: TAudioSessionStatus) => {
        sessionStatusRef.current = status;
        setSessionStatus(status);
    }, []);

    const updateTranscripts = useCallback((updater: IChatMessage[] | ((prev: IChatMessage[]) => IChatMessage[])) => {
        setChats((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            transcriptsRef.current = next;
            return next;
        });
    }, [setChats]);

    const clearSilenceTimer = useCallback(() => {
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        };
    }, []);

    const clearRecognitionTimers = useCallback(() => {
        clearSilenceTimer();
        if (restartTimerRef.current) {
            clearTimeout(restartTimerRef.current);
            restartTimerRef.current = null;
        };
        if (startWatchdogRef.current) {
            clearTimeout(startWatchdogRef.current);
            startWatchdogRef.current = null;
        };
        if (deadEngineWatchdogRef.current) {
            clearTimeout(deadEngineWatchdogRef.current);
            deadEngineWatchdogRef.current = null;
        };
    }, [clearSilenceTimer]);

    const clearLiveTranscript = useCallback(() => {
        pendingFinalRef.current = '';
        interimTranscriptRef.current = '';
        setInterimTranscript('');
    }, []);

    const getLiveTranscript = useCallback(() => {
        return mergeSpokenText(pendingFinalRef.current, interimTranscriptRef.current);
    }, []);

    const detachRecognition = useCallback((recognition: IBrowserSpeechRecognition) => {
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.onstart = null;
        recognition.onspeechstart = null;
        recognition.onspeechend = null;
    }, []);

    // Chrome often ends a recognition pass while speech is still only "interim".
    // Promote it so the next pass / send does not drop those words.
    const promoteInterimToPending = useCallback(() => {
        const interim = interimTranscriptRef.current.trim();
        if (!interim) return;

        pendingFinalRef.current = mergeSpokenText(pendingFinalRef.current, interim);
        interimTranscriptRef.current = '';
        setInterimTranscript(pendingFinalRef.current);
    }, []);

    const canListen = useCallback(() => {
        return (
            sessionActiveRef.current
            && !isMicMutedRef.current
            && !isProcessingTurnRef.current
            && !isFlushingRef.current
            && messageThresholdRef.current !== 'level2'
        );
    }, []);

    const stopRecognition = useCallback(() => {
        shouldRestartRecognitionRef.current = false;
        isFlushingRef.current = false;
        isSpeechActiveRef.current = false;
        recognitionStartedRef.current = false;
        clearRecognitionTimers();
        clearLiveTranscript();

        const recognition = recognitionRef.current;
        recognitionRef.current = null;
        if (!recognition) return;

        detachRecognition(recognition);

        try {
            recognition.abort();
        } catch {
            try {
                recognition.stop();
            } catch {
                // already stopped
            };
        };
    }, [clearLiveTranscript, clearRecognitionTimers, detachRecognition]);

    const flushRecognition = useCallback((): Promise<void> => {
        return new Promise((resolve) => {
            const recognition = recognitionRef.current;
            shouldRestartRecognitionRef.current = false;
            clearSilenceTimer();

            if (!recognition) {
                promoteInterimToPending();
                resolve();
                return;
            };

            isFlushingRef.current = true;
            let settled = false;

            let flushTimer: ReturnType<typeof setTimeout> | null = null;
            const finish = () => {
                if (settled) return;
                settled = true;
                if (flushTimer) clearTimeout(flushTimer);
                isFlushingRef.current = false;
                isSpeechActiveRef.current = false;
                recognitionStartedRef.current = false;
                promoteInterimToPending();
                if (recognitionRef.current === recognition) {
                    recognitionRef.current = null;
                };
                detachRecognition(recognition);
                resolve();
            };

            recognition.onend = () => finish();
            recognition.onerror = () => finish();
            flushTimer = setTimeout(finish, FLUSH_TIMEOUT_MS);

            try {
                recognition.stop();
            } catch {
                finish();
            };
        });
    }, [clearSilenceTimer, detachRecognition, promoteInterimToPending]);

    const clearPersonaTyping = useCallback(() => {
        typingCleanupRef.current?.();
        typingCleanupRef.current = null;
        setPersonaTypedText('');
    }, []);

    const startSmoothPersonaTyping = useCallback((text: string, getProgress: () => number) => {
        clearPersonaTyping();
        if (!text.trim()) return;

        setPersonaTypedText('');
        let rafId = 0;
        let displayedLen = 0;
        let lastNow = performance.now();

        const tick = (now: number) => {
            const dt = Math.min(0.05, Math.max(0, (now - lastNow) / 1000));
            lastNow = now;

            const progress = Math.min(1, Math.max(0, getProgress()));
            const targetLen = progress >= 1 ? text.length : text.length * progress;
            const diff = targetLen - displayedLen;

            // Ease toward the speech-synced target so char reveals don't jump in chunks.
            if (diff > 0) {
                const eased = displayedLen + diff * Math.min(1, 14 * dt);
                const floorCatchUp = displayedLen + Math.min(diff, 90 * dt);
                displayedLen = Math.max(eased, floorCatchUp);
            } else if (progress >= 1) {
                displayedLen = text.length;
            };

            const visibleLen = progress >= 1 ? text.length : Math.floor(displayedLen);
            setPersonaTypedText(text.slice(0, visibleLen));

            if (progress < 1 || visibleLen < text.length) {
                rafId = requestAnimationFrame(tick);
            } else {
                setPersonaTypedText(text);
            };
        };

        rafId = requestAnimationFrame(tick);
        typingCleanupRef.current = () => cancelAnimationFrame(rafId);
    }, [clearPersonaTyping]);

    const startTimedPersonaTyping = useCallback((text: string, durationMs: number) => {
        const start = performance.now();
        const duration = Math.max(durationMs, 1);
        startSmoothPersonaTyping(text, () => (performance.now() - start) / duration);
    }, [startSmoothPersonaTyping]);

    const startAudioSyncedPersonaTyping = useCallback((audio: HTMLAudioElement, text: string) => {
        const estimatedMs = Math.min(60000, Math.max(2500, text.trim().split(/\s+/).length * 400));
        const start = performance.now();

        startSmoothPersonaTyping(text, () => {
            const duration = audio.duration;
            if (Number.isFinite(duration) && duration > 0) {
                return audio.currentTime / duration;
            };
            return (performance.now() - start) / estimatedMs;
        });
    }, [startSmoothPersonaTyping]);

    const stopSpeech = useCallback(() => {
        clearPersonaTyping();

        if (audioPlayerRef.current) {
            audioPlayerRef.current.onended = null;
            audioPlayerRef.current.onerror = null;
            audioPlayerRef.current.pause();
            audioPlayerRef.current.src = '';
            audioPlayerRef.current = null;
        };
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        };
    }, [clearPersonaTyping]);

    const speakBrowserTts = useCallback((text: string): Promise<void> => {
        return new Promise((resolve) => {
            if (!('speechSynthesis' in window) || !text.trim()) {
                resolve();
                return;
            };

            window.speechSynthesis.cancel();

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.rate = 1;
            utterance.pitch = 1;

            let settled = false;
            const finish = () => {
                if (settled) return;
                settled = true;
                setPersonaTypedText(text);
                resolve();
            };

            // Chrome sometimes fails to fire onend — use a length-based fallback.
            const fallbackMs = Math.min(30000, Math.max(2500, text.trim().split(/\s+/).length * 350));
            startTimedPersonaTyping(text, fallbackMs);
            const fallbackTimer = setTimeout(finish, fallbackMs);

            utterance.onend = () => {
                clearTimeout(fallbackTimer);
                finish();
            };
            utterance.onerror = () => {
                clearTimeout(fallbackTimer);
                finish();
            };

            window.speechSynthesis.speak(utterance);
        });
    }, [startTimedPersonaTyping]);

    const speakText = useCallback((text: string, audioBase64?: string | null): Promise<void> => {
        stopSpeech();

        if (!text.trim()) {
            return Promise.resolve();
        };

        if (audioBase64) {
            return new Promise((resolve) => {
                const audio = new Audio(`data:audio/mpeg;base64,${audioBase64}`);
                audioPlayerRef.current = audio;
                startAudioSyncedPersonaTyping(audio, text);

                let settled = false;
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    setPersonaTypedText(text);
                    if (audioPlayerRef.current === audio) {
                        audioPlayerRef.current = null;
                    };
                    resolve();
                };

                const fallbackMs = Math.min(60000, Math.max(2500, text.trim().split(/\s+/).length * 400));
                const fallbackTimer = setTimeout(finish, fallbackMs);

                audio.onended = () => {
                    clearTimeout(fallbackTimer);
                    finish();
                };
                audio.onerror = () => {
                    clearTimeout(fallbackTimer);
                    void speakBrowserTts(text).then(finish);
                };

                void audio.play().catch(() => {
                    clearTimeout(fallbackTimer);
                    void speakBrowserTts(text).then(finish);
                });
            });
        };

        return speakBrowserTts(text);
    }, [speakBrowserTts, startAudioSyncedPersonaTyping, stopSpeech]);

    const scheduleSilenceCommit = useCallback(() => {
        clearSilenceTimer();
        silenceTimerRef.current = setTimeout(() => {
            if (isFlushingRef.current) {
                scheduleSilenceCommit();
                return;
            };
            if (isSpeechActiveRef.current && Date.now() - lastResultAtRef.current < SILENCE_MS) {
                scheduleSilenceCommit();
                return;
            };
            void handleUserUtteranceEndRef.current();
        }, SILENCE_MS);
    }, [clearSilenceTimer]);

    const scheduleRecognitionRestart = useCallback(() => {
        if (restartTimerRef.current) {
            clearTimeout(restartTimerRef.current);
        };
        restartTimerRef.current = setTimeout(() => {
            restartTimerRef.current = null;
            if (
                shouldRestartRecognitionRef.current
                && canListen()
            ) {
                startListeningRef.current();
            };
        }, RESTART_MS);
    }, [canListen]);

    const armDeadEngineWatchdog = useCallback(() => {
        if (deadEngineWatchdogRef.current) {
            clearTimeout(deadEngineWatchdogRef.current);
        };
        deadEngineWatchdogRef.current = setTimeout(() => {
            if (
                shouldRestartRecognitionRef.current
                && canListen()
                && sessionStatusRef.current === 'listening'
            ) {
                startListeningRef.current({ force: true });
            };
        }, DEAD_ENGINE_MS);
    }, [canListen]);

    const pauseVoiceInput = useCallback(() => {
        stopRecognition();
        stopSpeech();
    }, [stopRecognition, stopSpeech]);

    const startListening = useCallback((options?: { force?: boolean }) => {
        if (!canListen()) return;

        const SpeechRecognitionCtor = getSpeechRecognitionConstructor();
        if (!SpeechRecognitionCtor) {
            setIsSpeechSupported(false);
            return;
        };

        if (!options?.force && recognitionRef.current && recognitionStartedRef.current) {
            return;
        };

        promoteInterimToPending();

        const previous = recognitionRef.current;
        recognitionRef.current = null;
        recognitionStartedRef.current = false;
        if (previous) {
            detachRecognition(previous);
            try {
                previous.abort();
            } catch {
                // ignore
            };
        };

        const recognition = new SpeechRecognitionCtor();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
        recognition.lang = 'en-US';
        recognitionRef.current = recognition;
        shouldRestartRecognitionRef.current = true;
        isSpeechActiveRef.current = false;
        updateSessionStatus('listening');

        recognition.onstart = () => {
            recognitionStartedRef.current = true;
            if (startWatchdogRef.current) {
                clearTimeout(startWatchdogRef.current);
                startWatchdogRef.current = null;
            };
            updateSessionStatus('listening');
            armDeadEngineWatchdog();
        };

        recognition.onspeechstart = () => {
            isSpeechActiveRef.current = true;
            clearSilenceTimer();
            armDeadEngineWatchdog();
        };

        recognition.onspeechend = () => {
            isSpeechActiveRef.current = false;
            armDeadEngineWatchdog();
            if (getLiveTranscript()) {
                scheduleSilenceCommit();
            };
        };

        recognition.onresult = (event: ISpeechRecognitionEvent) => {
            if (!sessionActiveRef.current || isMicMutedRef.current) return;
            if (isProcessingTurnRef.current && !isFlushingRef.current) return;

            lastResultAtRef.current = Date.now();
            armDeadEngineWatchdog();

            let interim = '';
            let finalChunk = '';

            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const result = event.results[i];
                const alternative = result[0] || result.item?.(0);
                const text = alternative?.transcript?.trim() ?? '';
                if (!text) continue;

                if (result.isFinal) {
                    finalChunk = `${finalChunk} ${text}`.trim();
                } else {
                    interim = `${interim} ${text}`.trim();
                };
            };

            if (finalChunk) {
                pendingFinalRef.current = mergeSpokenText(pendingFinalRef.current, finalChunk);
            };

            interimTranscriptRef.current = interim;
            const liveText = getLiveTranscript();
            setInterimTranscript(liveText);

            if (liveText && !isFlushingRef.current) {
                scheduleSilenceCommit();
            };
        };

        recognition.onerror = (event: ISpeechRecognitionErrorEvent) => {
            if (event.error === 'aborted') return;

            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                setIsSpeechSupported(false);
                sessionActiveRef.current = false;
                setIsSessionActive(false);
                stopRecognition();
                updateSessionStatus('idle');
                window.alert('Microphone permission is required for voice conversation.');
                return;
            };

            promoteInterimToPending();
            recognitionStartedRef.current = false;

            // Prefer onend to restart. Chrome sometimes skips onend after no-speech/network.
            setTimeout(() => {
                if (
                    shouldRestartRecognitionRef.current
                    && canListen()
                    && !recognitionStartedRef.current
                    && (recognitionRef.current === recognition || !recognitionRef.current)
                ) {
                    startListeningRef.current({ force: true });
                };
            }, 350);
        };

        recognition.onend = () => {
            promoteInterimToPending();
            recognitionStartedRef.current = false;
            isSpeechActiveRef.current = false;

            if (recognitionRef.current === recognition) {
                recognitionRef.current = null;
            };

            if (shouldRestartRecognitionRef.current && canListen()) {
                scheduleRecognitionRestart();
            };
        };

        try {
            recognition.start();
        } catch {
            recognitionStartedRef.current = false;
            if (recognitionRef.current === recognition) {
                recognitionRef.current = null;
            };
            scheduleRecognitionRestart();
        };

        if (startWatchdogRef.current) {
            clearTimeout(startWatchdogRef.current);
        };
        startWatchdogRef.current = setTimeout(() => {
            if (recognitionRef.current === recognition && !recognitionStartedRef.current) {
                try {
                    recognition.abort();
                } catch {
                    // ignore
                };
                if (recognitionRef.current === recognition) {
                    recognitionRef.current = null;
                };
                if (shouldRestartRecognitionRef.current && canListen()) {
                    startListeningRef.current({ force: true });
                };
            };
        }, START_WATCHDOG_MS);
    }, [
        armDeadEngineWatchdog,
        canListen,
        clearSilenceTimer,
        detachRecognition,
        getLiveTranscript,
        promoteInterimToPending,
        scheduleRecognitionRestart,
        scheduleSilenceCommit,
        stopRecognition,
        updateSessionStatus,
    ]);

    const handleUserUtteranceEnd = useCallback(async () => {
        if (
            isProcessingTurnRef.current
            || isFlushingRef.current
            || !sessionActiveRef.current
            || isMicMutedRef.current
            || messageThresholdRef.current === 'level2'
        ) {
            return;
        };

        if (!getLiveTranscript()) {
            return;
        };

        clearSilenceTimer();

        const resumeListening = () => {
            isProcessingTurnRef.current = false;
            if (sessionActiveRef.current && !isMicMutedRef.current && messageThresholdRef.current !== 'level2') {
                shouldRestartRecognitionRef.current = true;
                startListening();
            } else {
                updateSessionStatus('idle');
            };
        };

        await flushRecognition();

        const userText = getLiveTranscript();
        if (!userText || !conversationId || !sessionActiveRef.current) {
            resumeListening();
            return;
        };

        isProcessingTurnRef.current = true;
        clearLiveTranscript();
        updateSessionStatus('processing');

        const optimisticUserMessage: IChatMessage = {
            by: EChatSource.PLAYER,
            message: userText,
            timestamp: new Date().toISOString(),
        };
        updateTranscripts((prev) => [...prev, optimisticUserMessage]);

        try {
            const result = await generateAudioResponse({
                conversation_id: conversationId,
                message: userText,
            });

            if (!sessionActiveRef.current) {
                isProcessingTurnRef.current = false;
                return;
            };

            let nextTranscripts: IChatMessage[] = [];
            updateTranscripts((prev) => {
                const withoutOptimistic = prev.slice(0, -1);
                nextTranscripts = [
                    ...withoutOptimistic,
                    {
                        by: result.user_message.source,
                        message: result.user_message.message,
                        timestamp: result.user_message.created_at,
                    },
                    {
                        by: result.persona_message.source,
                        message: result.persona_message.message,
                        timestamp: result.persona_message.created_at,
                        typingEffect: true,
                    },
                ];
                return nextTranscripts;
            });

            updateSessionStatus('speaking');
            await speakText(result.persona_message.message, result.audio_base64);

            isProcessingTurnRef.current = false;

            if (!sessionActiveRef.current) return;

            const reachedThreshold = evaluateMessageThreshold(nextTranscripts);
            if (reachedThreshold) {
                messageThresholdRef.current = reachedThreshold;
            };

            if (reachedThreshold === 'level2') {
                pauseVoiceInput();
                updateSessionStatus('idle');
                return;
            };

            if (!isMicMutedRef.current) {
                startListening();
            } else {
                updateSessionStatus('idle');
            };
        } catch {
            isProcessingTurnRef.current = false;
            if (sessionActiveRef.current && !isMicMutedRef.current) {
                startListening();
            } else {
                updateSessionStatus('idle');
            };
        };
    }, [
        clearLiveTranscript,
        clearSilenceTimer,
        conversationId,
        evaluateMessageThreshold,
        flushRecognition,
        generateAudioResponse,
        getLiveTranscript,
        pauseVoiceInput,
        speakText,
        startListening,
        updateSessionStatus,
        updateTranscripts,
    ]);

    useEffect(() => {
        handleUserUtteranceEndRef.current = handleUserUtteranceEnd;
    }, [handleUserUtteranceEnd]);

    useEffect(() => {
        startListeningRef.current = startListening;
    }, [startListening]);

    useEffect(() => {
        setIsSpeechSupported(!!getSpeechRecognitionConstructor());
    }, []);

    useEffect(() => {
        if (isCompletionWindowVisible) {
            pauseVoiceInput();
            isProcessingTurnRef.current = false;
            updateSessionStatus('idle');
        };
    }, [isCompletionWindowVisible, pauseVoiceInput, updateSessionStatus]);

    // Resume listening after user chooses "Stay in Conversation"
    useEffect(() => {
        if (
            continueMode
            && !isCompletionWindowVisible
            && isSessionActive
            && !isMicMuted
            && messageThreshold !== 'level2'
            && sessionStatusRef.current === 'idle'
            && !isProcessingTurnRef.current
        ) {
            startListening();
        };
    }, [continueMode, isCompletionWindowVisible, isMicMuted, isSessionActive, messageThreshold, startListening]);

    useEffect(() => {
        if (!isTranscriptOpen || !transcriptBodyRef.current) return;
        const body = transcriptBodyRef.current;
        body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
    }, [chats, interimTranscript, sessionStatus, isTranscriptOpen]);

    useEffect(() => {
        // Clear any leftover TTS from a previous session/navigation.
        stopSpeech();

        // Cancel TTS on refresh/close — React unmount alone is not always enough.
        window.addEventListener('pagehide', stopSpeech);
        window.addEventListener('beforeunload', stopSpeech);

        return () => {
            sessionActiveRef.current = false;
            isProcessingTurnRef.current = false;
            stopRecognition();
            stopSpeech();
            window.removeEventListener('pagehide', stopSpeech);
            window.removeEventListener('beforeunload', stopSpeech);
        };
    }, [stopRecognition, stopSpeech]);

    const onStartConversation = async () => {
        if (!personaDetails || !conversationId || !isSpeechSupported || sessionActiveRef.current) return;
        if (messageThreshold === 'level2') {
            setIsCompletionWindowVisible(true);
            return;
        };

        sessionActiveRef.current = true;
        setIsSessionActive(true);
        isMicMutedRef.current = false;
        isProcessingTurnRef.current = false;
        setIsMicMuted(false);

        // Opening is already saved when the conversation was created — prefer hydrated chats.
        if (!transcriptsRef.current.length) {
            const openingMessage: IChatMessage = {
                by: EChatSource.PERSONA,
                message: personaDetails.opening,
                timestamp: new Date().toISOString(),
            };
            updateTranscripts([openingMessage]);
            evaluateMessageThreshold([openingMessage]);
        };

        updateSessionStatus('preparing');

        let spokenMessage = [...transcriptsRef.current]
            .reverse()
            .find((item) => item.by === EChatSource.PERSONA)
            ?.message
            || personaDetails.opening;
        let spokenAudio: string | null = null;

        try {
            const synthesized = await synthesizeAudio({ conversation_id: conversationId });
            if (!sessionActiveRef.current) return;

            spokenMessage = synthesized.message || spokenMessage;
            spokenAudio = synthesized.audio_base64;
        } catch {
            if (!sessionActiveRef.current) return;
            // Fall back to browser TTS below if synthesize fails.
        };

        updateSessionStatus('speaking');
        await speakText(spokenMessage, spokenAudio);

        if (!sessionActiveRef.current) return;

        isProcessingTurnRef.current = false;
        startListening();
    };

    const onEndCall = () => {
        sessionActiveRef.current = false;
        setIsSessionActive(false);
        isMicMutedRef.current = false;
        isProcessingTurnRef.current = false;
        setIsMicMuted(false);
        stopRecognition();
        stopSpeech();
        updateSessionStatus('idle');
        clearLiveTranscript();
    };

    const handleNext = () => {
        pauseVoiceInput();
        sessionActiveRef.current = false;
        setIsSessionActive(false);
        completeOrPromptNext();
    };

    const statusLabel = (() => {
        if (!isSpeechSupported) return 'Speech recognition is not supported in this browser';
        if (messageThreshold === 'level2') return 'Conversation limit reached';
        if (sessionStatus === 'listening') return 'Listening... speak naturally';
        if (sessionStatus === 'preparing') return 'Connecting...';
        if (sessionStatus === 'processing') return 'Thinking...';
        if (sessionStatus === 'speaking') return `${personaDetails?.person ?? 'Persona'} is speaking`;
        if (isMicMuted) return 'Microphone muted';
        if (chats.length > 0) return 'Press start to continue';
        return 'Press start to begin';
    })();

    const showStartControls = !isSessionActive && sessionStatus === 'idle' && !isMicMuted;

    const activeCaption = (() => {
        if (interimTranscript) {
            return { speaker: 'you' as const, text: interimTranscript, live: true, thinking: false, typing: false };
        };

        if (sessionStatus === 'processing') {
            return { speaker: 'persona' as const, text: '', live: false, thinking: true, typing: false };
        };

        if (sessionStatus === 'speaking') {
            return {
                speaker: 'persona' as const,
                text: personaTypedText,
                live: true,
                thinking: false,
                typing: true,
            };
        };

        // Only show live captions — don't surface past transcript messages in the scene.
        return null;
    })();

    const transcriptCount = chats.length + (interimTranscript ? 1 : 0);

    return (
        <div
            className={`mbts_audio_convo__scene mbts_audio_convo__scene--${sessionStatus}${isSessionActive ? ' mbts_audio_convo__scene--live' : ''}${isTranscriptOpen ? ' mbts_audio_convo__scene--transcript-open' : ''}`}
        >
                        <div className='mbts_audio_convo__media' aria-hidden='true'>
                            {bgMedia === 'video' ? (
                                <video
                                    className='mbts_audio_convo__bg'
                                    src='/assets/mbts_audio_convo.mp4'
                                    poster='/assets/mbts_audio_convo.png'
                                    autoPlay
                                    muted
                                    playsInline
                                />
                            ) : (
                                <img
                                    className='mbts_audio_convo__bg'
                                    src='/assets/mbts_audio_convo_2.png'
                                    alt=''
                                />
                            )}
                            <div className='mbts_audio_convo__scrim mbts_audio_convo__scrim--top' />
                            <div className='mbts_audio_convo__scrim mbts_audio_convo__scrim--bottom' />
                        </div>

                        <div className='mbts_audio_convo__header'>
                            <div className='mbts_audio_convo__identity'>
                                <p className='mbts_audio_convo__name'>{personaDetails.person}</p>
                                <p className='mbts_audio_convo__role'>{personaDetails.title}</p>
                            </div>
                            <div className='mbts_audio_convo__header__meta'>
                                {/* TEMP: remove after bg media is decided */}
                                {/* <button
                                    type='button'
                                    className='mbts_audio_convo__bg_toggle'
                                    onClick={() => setBgMedia((prev) => (prev === 'image' ? 'video' : 'image'))}
                                >
                                    BG: {bgMedia === 'image' ? 'Image' : 'Video'}
                                </button> */}
                                {isSessionActive && (
                                    <span className='mbts_audio_convo__live_badge'>
                                        <span className='mbts_audio_convo__live_dot' />
                                        In conversation
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className='mbts_audio_convo__body'>
                            {activeCaption && (
                                <div
                                    className={`mbts_audio_convo__caption${activeCaption.live ? ' mbts_audio_convo__caption--live' : ''}${activeCaption.speaker === 'you' ? ' mbts_audio_convo__caption--you' : ''}`}
                                >
                                    {activeCaption.thinking && (
                                        <Fragment>
                                            <p className='mbts_audio_convo__caption__label'>{personaDetails.person}</p>
                                            <div className='mbts_audio_convo__thinking' aria-label='Thinking'>
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                        </Fragment>
                                    )}
                                    {!activeCaption.thinking && (
                                        <Fragment>
                                            <p className='mbts_audio_convo__caption__label'>
                                                {activeCaption.speaker === 'persona' ? personaDetails.person : 'You'}
                                                {activeCaption.live ? ' · speaking' : ''}
                                            </p>
                                            <p
                                                className={`mbts_audio_convo__caption__text${activeCaption.typing ? ' mbts_audio_convo__caption__text--typing' : ''}`}
                                            >
                                                {activeCaption.text}
                                            </p>
                                        </Fragment>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className='mbts_audio_convo__controls'>
                            <Link to={`/mbts/${scenarioId}/personas`} className='mbts_audio_convo__nav_link'>
                                <MoveLeft />
                                <span>Back</span>
                            </Link>

                            <div className='mbts_audio_convo__controls__center'>
                                <div
                                    className={`mbts_audio_convo__status mbts_audio_convo__status--${sessionStatus}${isMicMuted || messageThreshold === 'level2' ? ' mbts_audio_convo__status--muted' : ''}`}
                                >
                                    <div className='mbts_audio_convo__waveform' aria-hidden='true'>
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <span key={index} style={{ '--bar-index': index } as CSSProperties} />
                                        ))}
                                    </div>
                                    <p className='mbts_audio_convo__status__label'>{statusLabel}</p>
                                </div>

                                {showStartControls ? (
                                    <button
                                        type='button'
                                        className='mbts_audio_convo__control mbts_audio_convo__control--start'
                                        onClick={onStartConversation}
                                        disabled={!isSpeechSupported || !conversationId || messageThreshold === 'level2'}
                                        aria-label='Start voice conversation'
                                    >
                                        <Play />
                                        <span>Start</span>
                                    </button>
                                ) : (
                                    <button
                                        type='button'
                                        className='mbts_audio_convo__control mbts_audio_convo__control--end'
                                        onClick={onEndCall}
                                        aria-label='End voice conversation'
                                    >
                                        <PhoneOff />
                                        <span>End</span>
                                    </button>
                                )}
                            </div>

                            <button
                                className='mbts_audio_convo__nav_btn'
                                disabled={isHandleNextDisabled()}
                                onClick={handleNext}
                            >
                                {isProcessingOverview
                                    ? <span className='btn-loader' />
                                    : <Fragment>
                                        {nextOperation === 'analysis'
                                            ? <span>View Analysis</span>
                                            : !!nextPersonaDetails
                                                ? <span>Next Persona</span>
                                                : <span>Overview</span>
                                        }
                                        <MoveRight />
                                    </Fragment>
                                }
                            </button>
                        </div>

                        <button
                            type='button'
                            className={`mbts_audio_convo__transcript_tab${isTranscriptOpen ? ' mbts_audio_convo__transcript_tab--open' : ''}`}
                            onClick={() => setIsTranscriptOpen((prev) => !prev)}
                            aria-expanded={isTranscriptOpen}
                            aria-controls='mbts_audio_convo__immersive-transcript-panel'
                        >
                            {isTranscriptOpen ? <ChevronRight /> : <ChevronLeft />}
                            <span>Transcript</span>
                            {transcriptCount > 0 && (
                                <em className='mbts_audio_convo__transcript_tab__count'>{transcriptCount}</em>
                            )}
                        </button>

                        <aside
                            id='mbts_audio_convo__immersive-transcript-panel'
                            className={`mbts_audio_convo__transcript_panel${isTranscriptOpen ? ' mbts_audio_convo__transcript_panel--open' : ''}`}
                            aria-hidden={!isTranscriptOpen}
                        >
                            <div className='mbts_audio_convo__transcript_panel__head'>
                                <div>
                                    <p className='mbts_audio_convo__transcript_panel__title'>Transcript</p>
                                    <p className='mbts_audio_convo__transcript_panel__subtitle'>
                                        Conversation with {personaDetails.person}
                                    </p>
                                </div>
                                <button
                                    type='button'
                                    className='mbts_audio_convo__transcript_panel__close'
                                    onClick={() => setIsTranscriptOpen(false)}
                                    aria-label='Close transcript'
                                >
                                    <ChevronRight />
                                </button>
                            </div>

                            <div
                                ref={transcriptBodyRef}
                                className='mbts_audio_convo__transcript_panel__body'
                                aria-live='polite'
                            >
                                {!chats.length && !interimTranscript && (
                                    <p className='mbts_audio_convo__transcript_panel__empty'>
                                        Your conversation will appear here.
                                    </p>
                                )}

                                {chats.map((chat, index) => (
                                    <div
                                        key={`${chat.timestamp}-${index}`}
                                        ref={index === chats.length - 1 && !interimTranscript && sessionStatus !== 'processing'
                                            ? lastMessageRef
                                            : undefined}
                                        className={`mbts_audio_convo__line mbts_audio_convo__line--${chat.by === EChatSource.PERSONA ? 'persona' : 'you'}`}
                                    >
                                        <span className='mbts_audio_convo__line__speaker'>
                                            {chat.by === EChatSource.PERSONA ? personaDetails.person : 'You'}
                                        </span>
                                        <p className='mbts_audio_convo__line__text'>{chat.message}</p>
                                    </div>
                                ))}

                                {!!interimTranscript && (
                                    <div
                                        ref={lastMessageRef}
                                        className='mbts_audio_convo__line mbts_audio_convo__line--you mbts_audio_convo__line--interim'
                                    >
                                        <span className='mbts_audio_convo__line__speaker'>You</span>
                                        <p className='mbts_audio_convo__line__text'>{interimTranscript}</p>
                                    </div>
                                )}

                                {sessionStatus === 'processing' && (
                                    <div
                                        ref={lastMessageRef}
                                        className='mbts_audio_convo__line mbts_audio_convo__line--persona'
                                    >
                                        <span className='mbts_audio_convo__line__speaker'>{personaDetails.person}</span>
                                        <div className='mbts_audio_convo__thinking' aria-label='Thinking'>
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>
    );
};
